/** Shared column-sort helpers used by Demands, Debtors, and Payments lists. */

export function defaultDirectionForSortType(sortType) {
  if (
    sortType === 'date' ||
    sortType === 'number' ||
    sortType === 'stage'
  ) {
    return 'desc';
  }
  return 'asc';
}

export function normalizeSortDirection(direction) {
  return direction === 'asc' ? 'asc' : 'desc';
}

/** Compare two sort values the same way Demands list columns do. */
export function compareSortValues(av, bv) {
  if (typeof av === 'number' && typeof bv === 'number') {
    return av - bv;
  }
  return String(av).localeCompare(String(bv), undefined, {
    sensitivity: 'base',
    numeric: true,
  });
}

/**
 * Sort rows by a column using a value extractor.
 * @param {unknown[]} rows
 * @param {{ columnId?: string, direction?: string }} sort
 * @param {(row: unknown, columnId: string) => string|number} getValue
 * @param {(row: unknown) => string} [getStableId]
 */
export function sortRowsByColumn(
  rows = [],
  sort = {},
  getValue,
  getStableId = (row) => String(row?.ROWID || row?.id || '')
) {
  const columnId = String(sort?.columnId || '');
  const direction = normalizeSortDirection(sort?.direction);
  const factor = direction === 'asc' ? 1 : -1;
  return [...(rows || [])].sort((a, b) => {
    const av = getValue(a, columnId);
    const bv = getValue(b, columnId);
    const cmp = compareSortValues(av, bv);
    if (cmp !== 0) return cmp * factor;
    return String(getStableId(a)).localeCompare(String(getStableId(b)));
  });
}

/**
 * Toggle/apply header sort the same way Demands does:
 * same column flips asc↔desc; new column uses sortType default direction.
 */
export function nextColumnSort(current, columnId, sortType) {
  const id = String(columnId || '');
  if (current?.columnId === id) {
    return {
      columnId: id,
      direction: current.direction === 'asc' ? 'desc' : 'asc',
    };
  }
  return {
    columnId: id,
    direction: defaultDirectionForSortType(sortType),
  };
}

export function loadPersistedSort(storageKey, defaultSort, isValidColumn) {
  const fallback = {
    columnId: defaultSort.columnId,
    direction: normalizeSortDirection(defaultSort.direction),
  };
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return { ...fallback };
    const parsed = JSON.parse(raw);
    const columnId = String(parsed?.columnId || '');
    const direction = normalizeSortDirection(parsed?.direction);
    if (!isValidColumn(columnId)) return { ...fallback };
    return { columnId, direction };
  } catch {
    return { ...fallback };
  }
}

export function savePersistedSort(storageKey, sort, defaultSort, isValidColumn) {
  const next = {
    columnId: isValidColumn(sort?.columnId) ? String(sort.columnId) : defaultSort.columnId,
    direction: normalizeSortDirection(sort?.direction),
  };
  localStorage.setItem(storageKey, JSON.stringify(next));
  return next;
}

export function sortStatusLabel(sort, labelForColumn) {
  const label = labelForColumn?.(sort?.columnId) || '';
  if (!label) return '';
  const direction = sort?.direction === 'asc' ? 'ascending' : 'descending';
  return `Sorted by ${label} (${direction})`;
}
